import type { InputHTMLAttributes } from 'react';
import { NumberFormatBase, type NumberFormatBaseProps } from 'react-number-format';
import { cleanDocument } from '@/app/helpers/cleanDocument';

/**
 * Input de CNPJ com máscara XX.XXX.XXX/XXXX-XX que aceita CNPJ alfanumérico
 * (IN RFB 2.229/2024, NT conjunta 2025.001): 12 primeiras posições [0-9A-Z] e
 * 2 dígitos verificadores numéricos. Substitui o PatternFormat nesses campos,
 * pois o placeholder '#' do PatternFormat aceita apenas dígitos.
 *
 * Em onValueChange: `values.value` é o CNPJ limpo em maiúsculas (sem máscara)
 * e `values.formattedValue` é o CNPJ mascarado — mesma semântica do PatternFormat.
 */
export type CnpjInputProps = Omit<
  NumberFormatBaseProps<InputHTMLAttributes<HTMLInputElement>>,
  'format' | 'removeFormatting' | 'isValidInputCharacter' | 'getCaretBoundary'
>;

const cleanCnpj = (value: string): string => {
  const clean = cleanDocument(value).slice(0, 14);
  // DVs (2 últimas posições) são sempre numéricos (NT 2025.001) — ignora letras digitadas ali
  return clean.slice(0, 12) + clean.slice(12).replace(/[^0-9]/g, '');
};

const applyCnpjMask = (value: string): string =>
  cleanCnpj(value)
    .replace(/^(.{2})(.)/, '$1.$2')
    .replace(/^(.{6})(.)/, '$1.$2')
    .replace(/^(.{10})(.)/, '$1/$2')
    .replace(/^(.{15})(.)/, '$1-$2');

export const CnpjInput = (props: CnpjInputProps) => (
  <NumberFormatBase
    {...props}
    format={applyCnpjMask}
    getCaretBoundary={(value) => new Array<boolean>(value.length + 1).fill(true)}
    isValidInputCharacter={(char) => /^[0-9a-z]$/i.test(char)}
    removeFormatting={cleanCnpj}
  />
);
